import { Location, PopStateEvent } from '@angular/common';
import { AbstractControl } from '@angular/forms';
import { Observable, defer, OperatorFunction, forkJoin, of, MonoTypeOperatorFunction, timer } from 'rxjs';
import { startWith, take, first, catchError, map, debounceTime, debounce } from 'rxjs/operators';

/* Functions */

interface ReplayValueOptions {
  debounce?: number
}
/**
 * Observable that starts with the current FormControl value at the time of subscription.
 * @param control - FormControl to subscribe to
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function replayValue(control: AbstractControl, { debounce }: ReplayValueOptions = {}): Observable<any> {
  return defer(() => {
    const operators: [OperatorFunction<unknown, unknown>] = [startWith(control.value)];
    if (debounce) {
      operators.unshift(debounceTime(debounce));
    }
    return control.valueChanges.pipe(...operators);
  });
}

/**
 * Emit initially and every time a location event fires.
 */
export function replayLocation(location: Location): Observable<PopStateEvent | undefined> {
  return new Observable(subscriber => {
    subscriber.next({ url: location.path?.(true) });
    return location.subscribe(event => {
      subscriber.next(event);
    });
  });
}

export interface Outcome<S, R, E = Error> {
  source: S
  result?: R
  error?: E
}

/**
 * Perform an operation on each item in an array and map them to either a successful result or a failure.
 * @param sources - array of anything
 * @param operation - function that maps a source item to an observable
 * @return An Outcome wrapping the source item along with a result or error.
 */
export function tryOperations<S, R, E>(sources: S[], operation: (source: S) => Observable<R>): Observable<Outcome<S, R, E>[]> {
  return forkJoin(
    sources.map(source => operation(source).pipe(
      map(result => ({ source, result })),
      catchError(error => of({ source, error })),
    ))
  );
}

/**
 * This will replace `obs$.pipe(first()).toPromise()` in RxJS 7
 * https://rxjs.dev/api/index/function/firstValueFrom
 */
export function firstValueFrom<T>(source: Observable<T>): Promise<T> {
  return source.pipe(first()).toPromise();
}

/* Operators */

/**
 * Take `count` emissions from the source observable, and emit the first value that matches a predicate.
 * @param count - The maximum number of source emissions to consider.
 * @param predicate - A function that evaluates a value emitted by the source Observable and returns a boolean.
 * @param defaultValue - Value to emit if no source emissions match the predicate, otherwise throw EmptyError.
 * @return A function that returns an Observable that emits the first value that satisfies the predicate function.
 */
export function takeFirst<T>(count: number, predicate: (value: T) => boolean, defaultValue?: T): MonoTypeOperatorFunction<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const firstArgs: any[] = arguments.length > 2 ? [predicate, defaultValue] : [predicate];
  return source => source.pipe(
    take(count),
    first(...firstArgs),
  );
}

/**
 * Take the first `count` emissions as they arrive. Debounce the rest by `dueTime` milliseconds.
 * @param count - The number of source emissions to take without debounce.
 * @param dueTime - Milliseconds to wait for debounce.
 * @return An operator function that debounces after a number of emissions.
 */
export function debounceAfter<T>(count: number, dueTime: number): MonoTypeOperatorFunction<T> {
  let index = 0;
  return source => source.pipe(
    debounce(() => {
      if (++index > count) {
        return timer(dueTime);
      }
      return of(0);
    }),
  );
}
